import React, { useEffect, useState } from 'react';
import { X, Save, Shield, AlertTriangle } from 'lucide-react';

const FirewallRuleModal = ({ open, onClose, onSave, initialData, existingRules = [], suggestedPriority = 100 }) => {
    const [formData, setFormData] = useState({
        direction: 'INGRESS',
        action: 'ALLOW',
        protocol: 'TCP',
        source_address: '0.0.0.0/0',
        destination_ports: '',
        priority: suggestedPriority,
        description: '',
        is_enabled: true
    });

    useEffect(() => {
        if (open) {
            setFormData(initialData || {
                direction: 'INGRESS',
                action: 'ALLOW',
                protocol: 'TCP',
                source_address: '0.0.0.0/0',
                destination_ports: '',
                priority: suggestedPriority,
                description: '',
                is_enabled: true
            });
        }
    }, [open, initialData, suggestedPriority]);

    if (!open) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-[#1e1e1e] border border-white/10 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-scaleIn">
                <div className="flex items-center justify-between p-6 border-b border-white/10 bg-[#252525]">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500/10 rounded-lg">
                            <Shield className="w-6 h-6 text-blue-500" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">
                                {initialData ? 'Kural Düzenle' : 'Yeni Kural Ekle'}
                            </h2>
                            <p className="text-sm text-gray-400">Güvenlik duvarı erişim kuralı</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Direction */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Yön (Trafiğin Yönü)</label>
                            <select
                                value={formData.direction}
                                onChange={e => setFormData({ ...formData, direction: e.target.value })}
                                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                            >
                                <option value="INGRESS">INGRESS (Gelen Trafik)</option>
                                <option value="EGRESS">EGRESS (Giden Trafik)</option>
                            </select>
                        </div>

                        {/* Action */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">İşlem (Karar)</label>
                            <select
                                value={formData.action}
                                onChange={e => setFormData({ ...formData, action: e.target.value })}
                                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                            >
                                <option value="ALLOW">✅ ALLOW (İzin Ver)</option>
                                <option value="DENY">🚫 DENY (Engelle)</option>
                                <option value="REJECT">✖ REJECT (Reddet)</option>
                            </select>
                        </div>

                        {/* Protocol */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Protokol</label>
                            <select
                                value={formData.protocol}
                                onChange={e => setFormData({ ...formData, protocol: e.target.value })}
                                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                            >
                                <option value="TCP">TCP</option>
                                <option value="UDP">UDP</option>
                                <option value="ICMP">ICMP</option>
                                <option value="ANY">ANY (Tümü)</option>
                            </select>
                        </div>

                        {/* Priority */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Öncelik (Priority)</label>
                            <input
                                type="number"
                                value={formData.priority}
                                onChange={e => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                                className={`w-full bg-[#1a1a1a] border rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-1 transition-colors ${existingRules.some(r => r.priority === formData.priority && r.id !== initialData?.id)
                                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                        : 'border-white/10 focus:border-blue-500 focus:ring-blue-500'
                                    }`}
                                placeholder="Örn: 100"
                            />
                            {existingRules.some(r => r.priority === formData.priority && r.id !== initialData?.id) && (
                                <div className="flex items-center gap-2 text-red-400 text-xs">
                                    <AlertTriangle className="w-3 h-3" />
                                    <span>Bu öncelik değeri zaten kullanılıyor! Önerilen: {suggestedPriority}</span>
                                </div>
                            )}
                            <p className="text-xs text-gray-500">Düşük sayı = Yüksek öncelik</p>
                        </div>

                        {/* Source Address */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Kaynak Adresi (CIDR)</label>
                            <input
                                type="text"
                                value={formData.source_address}
                                onChange={e => setFormData({ ...formData, source_address: e.target.value })}
                                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors font-mono"
                                placeholder="0.0.0.0/0"
                            />
                        </div>

                        {/* Destination Ports */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Hedef Portlar</label>
                            <input
                                type="text"
                                value={formData.destination_ports}
                                onChange={e => setFormData({ ...formData, destination_ports: e.target.value })}
                                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors font-mono"
                                placeholder="80, 443, 8000-9000"
                                disabled={formData.protocol === 'ICMP' || formData.protocol === 'ANY'}
                            />
                            <p className="text-xs text-gray-500">Virgül ile ayırarak veya aralık belirterek girin. (Protocol ANY/ICMP için geçersiz)</p>
                        </div>

                        {/* Description */}
                        <div className="col-span-1 md:col-span-2 space-y-2">
                            <label className="text-sm font-medium text-gray-300">Açıklama</label>
                            <input
                                type="text"
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                                placeholder="Kural açıklaması..."
                            />
                        </div>

                        {/* Status Toggle */}
                        <div className="col-span-1 md:col-span-2 bg-[#252525] p-4 rounded-lg flex items-center justify-between border border-white/5">
                            <div className="flex items-center gap-3">
                                {formData.is_enabled ? (
                                    <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
                                ) : (
                                    <div className="w-2 h-2 rounded-full bg-gray-500" />
                                )}
                                <div>
                                    <p className="font-medium text-white">Kural Durumu</p>
                                    <p className="text-sm text-gray-500">Bu kural etkinleştirilsin mi?</p>
                                </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.is_enabled}
                                    onChange={e => setFormData({ ...formData, is_enabled: e.target.checked })}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-lg shadow-blue-500/20"
                        >
                            <Save className="w-4 h-4" />
                            Kaydet
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FirewallRuleModal;
